import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";

import { Case, CaseDocumentWithPresignedUrl, Slide } from "pwapi";

import { ViewerMode } from "custom-models/ViewerTypes";

import { fetchCaseDiagnoses } from "shared/actions/caseDetailActions";
import { useViewableSlideStatuses } from "shared/hooks/slideHooks";
import { useSentDiagnosis } from "shared/hooks/useDiagnosis";
import { AuthState } from "shared/reducers/authReducer";
import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { SlideDetailState } from "shared/reducers/slideDetailReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";
import { dictationKeys } from "shared/utils/dictation";

import { ReactComponent as CountIcon } from "icons/count.svg";
import { ReactComponent as GrabIcon } from "icons/grab.svg";
import { ReactComponent as PencilIcon } from "icons/pencil.svg";
import { ReactComponent as PolygonIcon } from "icons/polygon.svg";
import { ReactComponent as RulerIcon } from "icons/ruler.svg";
import { ReactComponent as SidebarIcon } from "icons/sidebar.svg";
import { ReactComponent as TextIcon } from "icons/text.svg";
import ApplicationState from "store/ApplicationState";

import AIButton from "./AIButton";
import AnnotationToolButton from "./AnnotationToolButton";
import { DialogEnum } from "./dialogs/Dialog";
import DialogButton from "./dialogs/DialogButton";
import {
  canSignoutCase,
  isFinalized,
  isScreened,
  slideClassification,
  wasSignedOutFromWPL,
} from "./dialogs/utils";
import FlaggingTool from "./FlaggingTool";

type SlideButtonsProps = {
  caseDetailObject: CaseDetailState;
  slideDetailObject: SlideDetailState;
  authObject: AuthState;
  dialog: DialogEnum | null;
  setDialog: Function;
  archived: Boolean;
  sideWindowOpen: Boolean;
  setSideWindowOpen: Function;
  document?: CaseDocumentWithPresignedUrl;
  specimenId?: number;
};
type DiagnosisDialogType = "input" | "flat" | "pending" | "wpl" | undefined;

export const whichDiagnosisDialog = (
  caseDetailObject: CaseDetailState,
  slideDetailObject: SlideDetailState,
  authObject: AuthState,
  viewableSlideStatuses: Slide.status[],
  canSignOutInViewer?: boolean,
  noDeliverableDiagnosis?: boolean
) => {
  // Note that useSentDiagnosis() is not actually a hook or it would not work here
  const sentDiagnosis = useSentDiagnosis(
    caseDetailObject,
    slideDetailObject.id
  );
  // Any value for caseDetailObject.status other than 'active' or 'finalized'
  // (e.g. "working" or "wpl-signout-sent") will map to undefined
  let dialogType: DiagnosisDialogType = undefined;

  const canSignOutCase = canSignoutCase(
    caseDetailObject,
    authObject.user,
    canSignOutInViewer
  );

  if (
    (caseDetailObject.is_screening && !caseDetailObject.finalize_assignment) ||
    !slideDetailObject.specimen_id
  ) {
    dialogType = undefined;
  } else if (
    // User can sign out the case
    canSignOutCase &&
    // Case has been screened but is back in the screening process (rescreening)
    (!isScreened(caseDetailObject.case_diagnosis) ||
      caseDetailObject.is_screening) &&
    ((noDeliverableDiagnosis && !!caseDetailObject.specimens?.length) ||
      (!noDeliverableDiagnosis &&
        slideDetailObject.specimen &&
        viewableSlideStatuses.includes(
          slideDetailObject.status || Slide.status.ERROR
        )))
  ) {
    dialogType = "input";
  } else if (
    caseDetailObject.status === Case.status.LIS_PENDING ||
    (isFinalized(caseDetailObject) && sentDiagnosis?.diagnosis)
  ) {
    dialogType = "pending";
  } else if (wasSignedOutFromWPL(caseDetailObject)) {
    dialogType = "wpl";
  } else if (
    (isScreened(caseDetailObject.case_diagnosis) && canSignOutCase) ||
    isFinalized(caseDetailObject)
  ) {
    dialogType = "flat";
  }

  return dialogType;
};

export const dialogTypeToEnum = (type: DiagnosisDialogType) => {
  switch (type) {
    case "input":
      return DialogEnum.InputDiagnosis;
    case "flat":
      return DialogEnum.FlatDiagnosis;
    case "pending":
      return DialogEnum.ReadOnlyDiagnosis;
    case "wpl":
      return DialogEnum.WplDiagnosis;
    default:
      return undefined;
  }
};

const SlideButtons = ({
  caseDetailObject,
  slideDetailObject,
  authObject,
  dialog,
  setDialog,
  archived,
  sideWindowOpen,
  setSideWindowOpen,
  document,
  specimenId,
}: SlideButtonsProps) => {
  const dispatch = useDispatch();

  const classificationInfo = slideClassification(slideDetailObject);

  const features: FeatureFlags = useSelector(
    ({ sys }: ApplicationState) => sys.features
  );

  // NOTE: We need to dig into the API to get "LIS-SENT" diagnosis,
  // ... in order to display them in a context-rich way (because the
  // truly "LIS-FINALIZED" diagnosis is "flat" and missing codes)
  useEffect(() => {
    if (caseDetailObject.status === Case.status.FINALIZED)
      caseDetailObject.id &&
        dispatch(
          fetchCaseDiagnoses(caseDetailObject.id, caseDetailObject.status)
        );
  }, [caseDetailObject.id, caseDetailObject.status]);

  const viewableSlideStatuses = useViewableSlideStatuses();

  // ... as such, sentDiagnosis will only be available after
  // the above full-diagnoses from API has been retrieved
  const dialogType = whichDiagnosisDialog(
    caseDetailObject,
    slideDetailObject,
    authObject,
    viewableSlideStatuses,
    features?.signOutInViewer,
    features?.noDeliverableDiagnosis
  );

  const specimen = (caseDetailObject.specimens || []).find((specimen) =>
    [
      slideDetailObject.specimen?.id,
      document?.specimen_id,
      specimenId,
    ].includes(specimen.id)
  );

  return (
    <div className="SlideButtons">
      <nav className="SlideButtons__dialogs">
        {specimen && (
          <DialogButton
            currentDialog={dialog}
            wantedDialog={DialogEnum.Specimen}
            setDialog={setDialog}
            hotkey="s, shift+s"
            title="Specimen"
          >
            <u>S</u>pecimen&nbsp;{specimen.name}
            {caseDetailObject.accession_prefix?.is_diagnostic &&
              (!specimen.gross_description || !specimen.clinical_history) && (
                <FontAwesomeIcon icon="exclamation-circle" />
              )}
          </DialogButton>
        )}
        {dialogType === "input" && (
          <DialogButton
            currentDialog={dialog}
            wantedDialog={DialogEnum.InputDiagnosis}
            setDialog={setDialog}
            hotkey="d, shift+d"
            title="Diagnosis"
            dictationKeys={dictationKeys}
            // TODO: AI needs to be an "if" AI is present
            // className="DialogButton--AI"
          >
            <u>D</u>iagnosis
          </DialogButton>
        )}
        {dialogType === "flat" && (
          <DialogButton
            currentDialog={dialog}
            wantedDialog={DialogEnum.FlatDiagnosis}
            setDialog={setDialog}
            hotkey="d, shift+d"
            title="Diagnosis"
            dictationKeys={dictationKeys}
          >
            <u>D</u>iagnosis
          </DialogButton>
        )}
        {dialogType === "pending" && (
          <DialogButton
            currentDialog={dialog}
            wantedDialog={DialogEnum.ReadOnlyDiagnosis}
            setDialog={setDialog}
            hotkey="d, shift+d"
            title="Diagnosis"
            dictationKeys={dictationKeys}
          >
            <u>D</u>iagnosis
          </DialogButton>
        )}
        {dialogType === "wpl" && (
          <DialogButton
            currentDialog={dialog}
            wantedDialog={DialogEnum.WplDiagnosis}
            setDialog={setDialog}
            hotkey="d, shift+d"
            title="Diagnosis"
            dictationKeys={dictationKeys}
          >
            <u>D</u>iagnosis
          </DialogButton>
        )}
        {!features?.mrmcStudy &&
          features.aiOverlay &&
          classificationInfo !== undefined && (
            <AIButton currentDialog={dialog} setDialog={setDialog} />
          )}
        <DialogButton
          currentDialog={dialog}
          wantedDialog={DialogEnum.StainOrdering}
          setDialog={setDialog}
          hotkey="o, shift+o"
          title="Stain Ordering"
        >
          <u>O</u>rders
        </DialogButton>
      </nav>

      {!features?.mrmcStudy && (
        <nav className="SlideButtons__tools">
          {!archived && !!slideDetailObject.id && (
            <>
              {slideDetailObject.status === Slide.status.SUCCESS && (
                <>
                  <AnnotationToolButton
                    slideDetailObject={slideDetailObject}
                    wantedViewerMode={ViewerMode.PAN_ZOOM}
                    authObject={authObject}
                    title="Pan/Zoom"
                  >
                    <GrabIcon className="svg-icon__large" />
                  </AnnotationToolButton>

                  <AnnotationToolButton
                    slideDetailObject={slideDetailObject}
                    wantedViewerMode={ViewerMode.MEASUREMENT}
                    authObject={authObject}
                    title="Measure"
                    mppRequired={true}
                  >
                    <RulerIcon className="svg-icon__large" />
                  </AnnotationToolButton>

                  <AnnotationToolButton
                    slideDetailObject={slideDetailObject}
                    wantedViewerMode={ViewerMode.PEN_DRAWING}
                    authObject={authObject}
                    title="Draw"
                  >
                    <PencilIcon className="svg-icon__large" />
                  </AnnotationToolButton>

                  <AnnotationToolButton
                    slideDetailObject={slideDetailObject}
                    wantedViewerMode={ViewerMode.TEXT}
                    authObject={authObject}
                    title="Text"
                  >
                    <TextIcon className="svg-icon__large" />
                  </AnnotationToolButton>
                  <AnnotationToolButton
                    slideDetailObject={slideDetailObject}
                    wantedViewerMode={ViewerMode.AREA_COUNT}
                    authObject={authObject}
                    title="Area Count"
                    mppRequired={true}
                  >
                    <CountIcon className="svg-icon__large" />
                  </AnnotationToolButton>
                  {features?.prospectiveAI &&
                    classificationInfo !== undefined && (
                      <AnnotationToolButton
                        wantedViewerMode={ViewerMode.ROI}
                        slideDetailObject={slideDetailObject}
                        authObject={authObject}
                        title="Region of Interest"
                        mppRequired={true}
                        maskRequired={true}
                      >
                        <PolygonIcon className="svg-icon__large" />
                      </AnnotationToolButton>
                    )}
                </>
              )}
              {(features?.stainOrdering || features?.frontendAdmin) && (
                <FlaggingTool
                  caseDetail={caseDetailObject}
                  slideDetail={slideDetailObject}
                />
              )}
            </>
          )}
          <button
            title="Annotations"
            onClick={() => {
              setSideWindowOpen(!sideWindowOpen);
            }}
            data-tip=""
            data-for="slide-side-window"
            className="side-window-opener"
          >
            <SidebarIcon
              className="svg-icon__large"
              style={{
                marginBottom: "1px",
              }}
            />
          </button>
          <ReactTooltip place="left" id="slide-side-window" type="info">
            Case Log
            {!!slideDetailObject.id ? " / Slide Annotations" : ""}
            {features.labWorkStatus &&
            caseDetailObject?.source === Case.source.PW
              ? " / Lab Status"
              : ""}
          </ReactTooltip>
        </nav>
      )}
    </div>
  );
};

export default SlideButtons;
